/**
 * An on/off switch, which the suite had no wrapper for.
 *
 * The screens that needed one imported react-daisyui's directly, which is the
 * one library import `ui/` exists to hold. `onChange` hands over the new state
 * rather than an event, matching every other control here.
 *
 * @module components/ui/Toggle
 */

import React, { type ReactNode, useId } from "react";

import { useLockedRefusal } from "./EditLock";
import { LabelledControl } from "./LabelledControl";

/** The sizes, exported so safelist.test.ts can walk them. */
export const TOGGLE_SIZES = ["sm", "md"] as const;
export type ToggleSize = (typeof TOGGLE_SIZES)[number];

const SIZE_CLASS: Record<ToggleSize, string> = { sm: "toggle-sm", md: "" };

export interface ToggleProps {
  /** The visible label, drawn beside the switch and wired through `htmlFor`. */
  label?: ReactNode;
  /** A line under the label, announced with the switch through `aria-describedby`. */
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Names it when no visible `label` sits beside it. */
  "aria-label"?: string;
  /** Ids of the text that qualifies it, when a note sits beside the label. */
  "aria-describedby"?: string;
  disabled?: boolean;
  /** Why it is refused: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  size?: ToggleSize;
  id?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <Toggle checked={on} onChange={setOn} aria-label="Require review for minor amendments" />
 * ```
 */
export function Toggle({
  checked,
  onChange,
  label = undefined,
  description = undefined,
  "aria-label": ariaLabel = undefined,
  "aria-describedby": describedBy = undefined,
  disabled: ownDisabled = false,
  size = "md",
  id = undefined,
  className = "",
  "data-testid": testId,
  ...reasonProps
}: ToggleProps): React.ReactElement {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the control is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabled, offersReason, governed, disabledReason } = useLockedRefusal(
    ownDisabled,
    reasonProps,
  );
  const reasonId = useId();
  const generatedId = useId();
  const descriptionId = useId();
  const controlId = id ?? generatedId;
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const described =
    [describedBy, description ? descriptionId : undefined, reason ? reasonId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  // `aria-disabled`, not `disabled`: a refused switch keeps its name and its
  // place in the tab order, the same way `Checkbox` and `Button` do. daisyUI
  // paints the refusal from `.toggle:disabled` only, so the fade is restored
  // here.
  const refused = disabled ? "opacity-20 cursor-not-allowed" : "";
  const toggle = (
    <input
      type="checkbox"
      id={controlId}
      className={`toggle toggle-primary ${SIZE_CLASS[size]} ${refused} ${className}`
        .replace(/\s+/g, " ")
        .trim()}
      checked={checked}
      aria-label={ariaLabel}
      aria-describedby={described}
      aria-disabled={disabled || undefined}
      onChange={(event) => {
        if (disabled) {
          return;
        }
        onChange(event.currentTarget.checked);
      }}
      data-testid={testId}
    />
  );
  // Under a lock provider the wrapper is always there, so closing the lock
  // does not remount the control and drop its focus.
  const control =
    offersReason || governed ? (
      <span className={reason ? "tooltip" : "contents"} data-tip={reason}>
        {toggle}
        {reason ? (
          <span id={reasonId} className="sr-only">
            {reason}
          </span>
        ) : null}
      </span>
    ) : (
      toggle
    );
  if (label === undefined && description === undefined) {
    return control;
  }
  return (
    <LabelledControl
      controlId={controlId}
      label={label}
      descriptionId={descriptionId}
      description={description}
    >
      {control}
    </LabelledControl>
  );
}
